/* eslint-disable react/prop-types */
/**
 * UsAiSuggestionDialog — Phase 5 of the US Portfolio.
 *
 * Mirrors the spirit of the CSE `PortfolioSuggestion` flow:
 *   * Shows the latest saved suggestion if one exists (persistent).
 *   * 24h cooldown gate. When can_generate=false:
 *       - Banner shows "Generated X hours ago. Next run in Y hours."
 *       - Refresh button is disabled (admins can force via API directly).
 *   * When can_generate=true the user gets a Cost-Confirm pre-screen
 *     before a real Claude call:
 *         "AI suggestions use Claude (~$0.10 estimated).
 *          Last run: …
 *          Next available: …
 *          Continue? [Cancel] [Run AI suggestion]"
 *   * On success the suggestion view renders:
 *       - Thesis card
 *       - Style commentary card
 *       - Sector recommendations bar
 *       - Stock picks table with allocation + reason
 *
 * Style-aware: every section labels the run with the portfolio's
 * investment_style so users see immediately whether they're getting
 * Growth / Balanced / Income advice.
 */
import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Sparkles, Loader2, AlertTriangle, Clock, RefreshCw, DollarSign,
  CheckCircle2, ArrowRight, Trash2, PlusCircle,
} from "lucide-react";
import { apiGet, apiPost, apiDelete } from "@/lib/api";
import { toast } from "sonner";

const fmtUsd = (v, opts = {}) => {
  if (v == null || isNaN(v)) return "—";
  const n = Number(v);
  if (opts.compact && Math.abs(n) >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  if (opts.compact && Math.abs(n) >= 1e3) return `$${(n / 1e3).toFixed(1)}K`;
  return `$${n.toLocaleString(undefined, { minimumFractionDigits: opts.dp ?? 2, maximumFractionDigits: opts.dp ?? 2 })}`;
};
const fmtPct = (v, d = 1) => (v == null || isNaN(v) ? "—" : `${Number(v).toFixed(d)}%`);
const fmtDate = (d) => (d ? new Date(d).toLocaleString() : "—");

const STYLE_META = {
  growth: { label: "Growth", desc: "Capital appreciation focus, not dividend focused.", colour: "text-purple-600 dark:text-purple-400" },
  balanced: { label: "Balanced", desc: "Mix of growth and value, sector-diversified.", colour: "text-primary" },
  income: { label: "Income", desc: "Dividend yield + stability focus.", colour: "text-emerald-600 dark:text-emerald-400" },
};

// ---- Cooldown / cost-confirm panel ----------------------------------------

const CooldownPanel = ({ cooldown, onGenerate, busy }) => {
  const can = cooldown.can_generate;
  return (
    <div className="space-y-3" data-testid="us-ai-cooldown-panel">
      <div className="rounded-md border border-primary/30 bg-primary/5 p-4 space-y-2">
        <div className="flex items-center gap-2 text-sm font-semibold text-primary">
          <DollarSign className="h-4 w-4" /> Cost confirmation
        </div>
        <div className="text-sm text-foreground/90 space-y-1">
          <div>
            AI suggestions use{" "}
            <span className="font-semibold">Claude Sonnet 4.5</span>
            {" "}(~<span className="font-mono">${cooldown.estimated_cost_usd}</span>{" "}
            estimated per run).
          </div>
          <div className="text-xs text-muted-foreground">
            Cooldown is <span className="font-medium">{cooldown.cooldown_hours} hours</span> per portfolio.
          </div>
        </div>
      </div>
      {!can ? (
        <div
          className="rounded-md border border-amber-500/30 bg-amber-500/5 p-4 space-y-1.5"
          data-testid="us-ai-cooldown-block"
        >
          <div className="flex items-center gap-2 text-sm font-semibold text-amber-700 dark:text-amber-400">
            <Clock className="h-4 w-4" /> Cooldown active
          </div>
          <div className="text-xs text-foreground/90">
            Last run: <span className="tabular-nums">{fmtDate(cooldown.last_run_at)}</span>
          </div>
          <div className="text-xs text-foreground/90">
            Next available: <span className="tabular-nums">{fmtDate(cooldown.next_available_at)}</span>
            {" "}(<span className="font-mono">{cooldown.hours_remaining}h</span> remaining)
          </div>
        </div>
      ) : (
        <div className="rounded-md border bg-muted/20 p-3 text-xs text-muted-foreground" data-testid="us-ai-cooldown-ready">
          {cooldown.last_run_at ? (
            <>Last run: <span className="tabular-nums">{fmtDate(cooldown.last_run_at)}</span>. Cooldown elapsed — ready for a new run.</>
          ) : (
            <>This is your first AI suggestion run for this portfolio.</>
          )}
        </div>
      )}
      <div className="flex justify-end">
        <Button
          onClick={onGenerate}
          disabled={!can || busy}
          data-testid="us-ai-run-btn"
        >
          {busy ? (
            <><Loader2 className="h-3.5 w-3.5 animate-spin mr-2" /> Running Claude…</>
          ) : (
            <><Sparkles className="h-3.5 w-3.5 mr-2" /> Run AI suggestion <ArrowRight className="h-3.5 w-3.5 ml-1" /></>
          )}
        </Button>
      </div>
    </div>
  );
};

// ---- Sector recommendations bar -------------------------------------------

const SectorRecBar = ({ recommendations }) => {
  const entries = Object.entries(recommendations || {})
    .map(([k, v]) => [k, Number(v) || 0])
    .filter(([, v]) => v > 0)
    .sort((a, b) => b[1] - a[1]);
  if (entries.length === 0) return null;
  const total = entries.reduce((s, [, v]) => s + v, 0) || 1;
  const palette = [
    "bg-primary", "bg-emerald-500", "bg-amber-500", "bg-purple-500",
    "bg-rose-500", "bg-sky-500", "bg-violet-500", "bg-orange-500",
    "bg-teal-500", "bg-fuchsia-500",
  ];
  return (
    <div className="space-y-2" data-testid="us-ai-sector-recs">
      <div className="flex h-3 rounded-full overflow-hidden border bg-muted">
        {entries.map(([k, v], i) => (
          <div
            key={k}
            className={`${palette[i % palette.length]} transition-all`}
            style={{ width: `${(v / total) * 100}%` }}
            title={`${k}: ${v}%`}
          />
        ))}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5 text-xs">
        {entries.map(([k, v], i) => (
          <div key={k} className="flex items-center gap-1.5">
            <span className={`h-2 w-2 rounded-sm ${palette[i % palette.length]}`} />
            <span className="text-foreground/90 truncate">{k}</span>
            <span className="ml-auto tabular-nums text-muted-foreground">{v.toFixed(1)}%</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ---- Suggestion view ------------------------------------------------------

const SuggestionView = ({
  suggestion, cooldown, onRefresh, onDismiss, onAddSelected,
  refreshing, adding,
  pickState, onTogglePick, onEditShares,
  selectedPicks, totalCost,
}) => {
  const styleKey = (suggestion.investment_style || "balanced").toLowerCase();
  const style = STYLE_META[styleKey] || STYLE_META.balanced;
  const target = Number(suggestion.investment_amount_usd) || 0;
  const diff = totalCost - target;
  const within = Math.abs(diff) <= Math.max(target * 0.01, 5);
  const totalCls = selectedPicks.length === 0
    ? "border-muted bg-muted/20 text-muted-foreground"
    : within
      ? "border-emerald-500/30 bg-emerald-500/5 text-emerald-700 dark:text-emerald-400"
      : "border-amber-500/30 bg-amber-500/5 text-amber-700 dark:text-amber-400";

  return (
    <div className="space-y-4" data-testid="us-ai-suggestion-view">
      {/* Header strip */}
      <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
        <Badge variant="outline" className="font-medium">
          <Sparkles className="h-3 w-3 mr-1" /> {style.label}
        </Badge>
        <Badge variant="secondary" className="font-mono">
          ${suggestion.investment_amount_usd?.toLocaleString()}
        </Badge>
        <span>Generated {fmtDate(suggestion.generated_at)}</span>
        <span>· Cost ~${suggestion.cost_usd}</span>
        <span className="ml-auto inline-flex items-center gap-1 text-amber-700 dark:text-amber-400">
          <Clock className="h-3 w-3" />
          {cooldown?.can_generate
            ? "Cooldown elapsed — ready to refresh"
            : `Next run in ${cooldown?.hours_remaining}h`}
        </span>
      </div>

      {/* Thesis card */}
      <div
        className="rounded-md border bg-background p-4 space-y-2"
        data-testid="us-ai-thesis"
      >
        <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Investment thesis</div>
        <p className="text-sm leading-relaxed text-foreground/90 whitespace-pre-wrap">
          {suggestion.thesis || "—"}
        </p>
      </div>

      {/* Style commentary */}
      <div
        className="rounded-md border border-primary/30 bg-primary/5 p-4 space-y-2"
        data-testid="us-ai-style-commentary"
      >
        <div className={`text-[11px] uppercase tracking-wide ${style.colour}`}>
          {style.label} style commentary
        </div>
        <p className="text-sm leading-relaxed text-foreground/90 whitespace-pre-wrap">
          {suggestion.style_commentary || "—"}
        </p>
      </div>

      {/* Sector recommendations */}
      <div className="rounded-md border bg-background p-4 space-y-3" data-testid="us-ai-sector-card">
        <div className="flex items-center justify-between">
          <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Recommended sector targets</div>
          <Badge variant="outline" className="text-[10px]">
            Diversification {suggestion.diversification_score}/100
          </Badge>
        </div>
        <SectorRecBar recommendations={suggestion.sector_recommendations} />
        <div className="flex items-center gap-3 text-xs text-muted-foreground border-t pt-2">
          <span>Expected portfolio yield:</span>
          <span className="font-medium text-foreground tabular-nums">
            {fmtPct(suggestion.expected_portfolio_dividend_yield_pct, 2)}
          </span>
        </div>
      </div>

      {/* Stock picks table — with selection */}
      <div className="rounded-md border overflow-hidden" data-testid="us-ai-picks-table">
        <div className="bg-muted/30 border-b px-4 py-2 flex items-center gap-2">
          <CheckCircle2 className="h-3.5 w-3.5 text-primary" />
          <div className="text-xs font-semibold uppercase tracking-wide">
            Stock picks ({suggestion.stock_picks?.length || 0})
          </div>
          <div className="ml-auto text-[11px] text-muted-foreground">
            Tick the picks you want · adjust shares · the budget redistributes automatically.
          </div>
        </div>
        <div className="divide-y">
          {(suggestion.stock_picks || []).map((p) => {
            const state = pickState[p.us_symbol] || {};
            const checked = !!state.selected;
            const shares = state.shares ?? p.shares;
            const lineCost = (Number(p.close_price) || 0) * (Number(shares) || 0);
            return (
              <div
                key={p.us_symbol}
                className={`px-4 py-3 flex flex-wrap gap-3 items-start transition-colors ${
                  checked ? "bg-primary/5" : ""
                }`}
                data-testid={`us-ai-pick-${p.us_symbol}`}
              >
                <Checkbox
                  checked={checked}
                  onCheckedChange={() => onTogglePick(p.us_symbol)}
                  aria-label={`Select ${p.us_symbol}`}
                  className="mt-1"
                  data-testid={`us-ai-pick-toggle-${p.us_symbol}`}
                />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium flex items-center gap-2">
                    <span className="font-mono text-primary">{p.us_symbol}</span>
                    <span className="text-foreground/90">· {p.company_name}</span>
                    <Badge variant="outline" className="text-[9px]">{p.sector}</Badge>
                    {p.composite_score != null && (
                      <Badge variant="outline" className="text-[9px]">
                        Score {Number(p.composite_score).toFixed(0)}
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1 leading-snug">
                    {p.reason}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Input
                    type="number"
                    min="0"
                    step="1"
                    value={shares}
                    onChange={(e) => onEditShares(
                      p.us_symbol, parseInt(e.target.value || "0", 10) || 0,
                    )}
                    disabled={!checked}
                    className="h-8 w-16 text-right tabular-nums"
                    data-testid={`us-ai-pick-shares-${p.us_symbol}`}
                  />
                  <div className="text-right tabular-nums shrink-0 min-w-[88px]">
                    <div className="text-[10px] text-muted-foreground">@ {fmtUsd(p.close_price)}</div>
                    <div className="text-sm font-semibold">{fmtUsd(lineCost, { dp: 0 })}</div>
                    {p.expected_annual_dividend_usd > 0 && (
                      <div className="text-[10px] text-emerald-600 dark:text-emerald-400">
                        +{fmtUsd(p.expected_annual_dividend_usd, { dp: 0 })}/yr div
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Running total strip */}
      <div
        className={`rounded-md border p-2.5 flex items-center justify-between text-sm ${totalCls}`}
        data-testid="us-ai-running-total"
      >
        <div className="flex items-center gap-2">
          <span className="font-medium">Selected:</span>
          <span className="tabular-nums">{selectedPicks.length} positions</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="tabular-nums font-semibold" data-testid="us-ai-running-total-value">
            {fmtUsd(totalCost)}
          </span>
          <span className="text-muted-foreground">/</span>
          <span className="tabular-nums">{fmtUsd(target)}</span>
          <span aria-hidden>
            {selectedPicks.length === 0 ? "—" : within ? "✅" : "⚠"}
          </span>
        </div>
      </div>
      {!within && Math.abs(diff) > 10 && selectedPicks.length > 0 && (
        <div
          className="rounded-md border border-muted-foreground/20 bg-muted/30 p-2.5 text-[12px] text-muted-foreground space-y-1"
          data-testid="us-ai-rounding-note"
        >
          <div className="flex items-baseline gap-2 text-foreground">
            <span className="text-base leading-none">ℹ️</span>
            <span>
              Difference: <span className="font-semibold tabular-nums">{fmtUsd(Math.abs(diff))}</span>{" "}
              ({diff > 0 ? "over" : "under"} target).
            </span>
          </div>
          <div className="leading-snug pl-6">
            This is normal — stocks are purchased in <strong className="text-foreground">whole shares</strong>.
            The remaining cash stays in your brokerage account and can be deployed in your next AI suggestion run.
          </div>
        </div>
      )}

      {/* Footer actions */}
      <div className="flex flex-wrap gap-2 justify-end pt-2 border-t">
        <Button
          variant="ghost"
          size="sm"
          onClick={onDismiss}
          data-testid="us-ai-dismiss-btn"
          disabled={adding}
        >
          <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Dismiss
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={onRefresh}
          disabled={!cooldown?.can_generate || refreshing || adding}
          data-testid="us-ai-refresh-btn"
        >
          {refreshing ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
          ) : (
            <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
          )}
          Refresh suggestion
        </Button>
        <Button
          size="sm"
          onClick={onAddSelected}
          disabled={selectedPicks.length === 0 || adding}
          data-testid="us-ai-add-selected-btn"
        >
          {adding ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
          ) : (
            <PlusCircle className="h-3.5 w-3.5 mr-1.5" />
          )}
          Add Selected to Portfolio ({selectedPicks.length})
        </Button>
      </div>
    </div>
  );
};

// ---- Main dialog ----------------------------------------------------------

const UsAiSuggestionDialog = ({
  open, onClose, portfolioId, investmentStyle = "balanced",
  onHoldingsChanged,
}) => {
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState(false);
  const [suggestion, setSuggestion] = useState(null);
  const [cooldown, setCooldown] = useState(null);
  const [error, setError] = useState(null);
  // Pick selection state — keyed by us_symbol → { selected: bool, shares: int }.
  const [pickState, setPickState] = useState({});

  /**
   * Redistribute the suggestion's investment_amount_usd across currently
   * selected picks proportional to each pick's `allocation_pct` (the AI's
   * original weight). When user unticks a pick, remaining picks pro-rata
   * absorb its budget so the running total still matches the target.
   */
  const _redistribute = (picks, prevState, totalAmount) => {
    const next = { ...prevState };
    const selected = (picks || []).filter((p) => next[p.us_symbol]?.selected);
    const totalWeight = selected.reduce(
      (acc, p) => acc + (Number(p.allocation_pct) || 0),
      0,
    );
    if (totalWeight <= 0 || !totalAmount) return next;
    selected.forEach((p) => {
      const targetUsd = ((Number(p.allocation_pct) || 0) / totalWeight) * totalAmount;
      const price = Number(p.close_price) || 0;
      const shares = price > 0 ? Math.max(0, Math.floor(targetUsd / price)) : 0;
      next[p.us_symbol] = { ...next[p.us_symbol], shares };
    });
    return next;
  };

  // Initialise pickState from suggestion. All picks pre-selected; we then
  // run an initial redistribute pass so the visible totals align with the
  // suggestion's `investment_amount_usd` rather than the floor-truncated
  // per-pick amounts the AI returned.
  const _initStateFromSuggestion = (s) => {
    const init = {};
    (s?.stock_picks || []).forEach((p) => {
      init[p.us_symbol] = {
        selected: true,
        shares: Number(p.shares) || 0,
      };
    });
    return _redistribute(
      s?.stock_picks || [],
      init,
      Number(s?.investment_amount_usd) || 0,
    );
  };

  const togglePick = (sym) => {
    setPickState((prev) => {
      const flipped = { ...prev, [sym]: { ...prev[sym], selected: !(prev[sym]?.selected) } };
      return _redistribute(
        suggestion?.stock_picks || [],
        flipped,
        Number(suggestion?.investment_amount_usd) || 0,
      );
    });
  };
  const editShares = (sym, shares) => {
    setPickState((prev) => ({ ...prev, [sym]: { ...prev[sym], shares } }));
  };

  const selectedPicks = useMemo(() => {
    if (!suggestion?.stock_picks) return [];
    return suggestion.stock_picks
      .filter((p) => pickState[p.us_symbol]?.selected && (pickState[p.us_symbol]?.shares || 0) > 0)
      .map((p) => ({ ...p, shares: pickState[p.us_symbol]?.shares ?? p.shares }));
  }, [suggestion, pickState]);

  const totalCost = useMemo(
    () => selectedPicks.reduce(
      (acc, p) => acc + (Number(p.close_price) || 0) * (Number(p.shares) || 0),
      0,
    ),
    [selectedPicks],
  );

  const load = useCallback(async () => {
    if (!portfolioId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await apiGet(`/us-portfolios/${portfolioId}/ai-suggestion`);
      setSuggestion(res.suggestion);
      setCooldown(res.cooldown);
      setPickState(_initStateFromSuggestion(res.suggestion));
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load suggestion");
    } finally {
      setLoading(false);
    }
  }, [portfolioId]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const generate = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await apiPost(
        `/us-portfolios/${portfolioId}/ai-suggestion/generate`,
        {},
      );
      setSuggestion(res);
      setPickState(_initStateFromSuggestion(res));
      const fresh = await apiGet(`/us-portfolios/${portfolioId}/ai-suggestion`);
      setCooldown(fresh.cooldown);
      toast.success("AI suggestion generated", {
        description: `${res.stock_picks?.length || 0} picks · ${(res.investment_style || "balanced").toUpperCase()} style.`,
      });
    } catch (e) {
      const msg = e?.response?.data?.detail || "AI suggestion failed";
      setError(msg);
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const dismiss = async () => {
    if (!suggestion?.suggestion_id) return;
    if (!window.confirm("Dismiss this AI suggestion? Cooldown still applies.")) return;
    try {
      await apiDelete(`/us-portfolios/${portfolioId}/ai-suggestion/${suggestion.suggestion_id}`);
      setSuggestion(null);
      setPickState({});
      toast.success("Suggestion dismissed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Dismiss failed");
    }
  };

  /**
   * Bulk-add the selected picks as real holdings on the portfolio. Each
   * pick becomes a separate POST /us-portfolios/{id}/holdings call so a
   * single failure (e.g. ticker not yet onboarded) doesn't abort the
   * whole batch — we tally executed/failed and surface the result.
   */
  const addSelectedToPortfolio = async () => {
    if (selectedPicks.length === 0) {
      toast.error("Tick at least one pick");
      return;
    }
    if (!window.confirm(
      `Add ${selectedPicks.length} positions for ${fmtUsd(totalCost)} to your portfolio?`
    )) return;
    setAdding(true);
    let added = 0;
    const failed = [];
    for (const p of selectedPicks) {
      try {
        await apiPost(`/us-portfolios/${portfolioId}/holdings`, {
          us_symbol: p.us_symbol,
          shares: Number(p.shares) || 0,
          purchase_price: Number(p.close_price) || 0,
          notes: `From AI suggestion ${suggestion.suggestion_id?.slice(0, 8) || ""}`.trim(),
        });
        added += 1;
      } catch (e) {
        failed.push({
          us_symbol: p.us_symbol,
          reason: e?.response?.data?.detail || e?.message || "add failed",
        });
      }
    }
    setAdding(false);
    if (failed.length > 0) {
      toast.warning(`Added ${added} · ${failed.length} failed`, {
        description: failed.map((f) => `${f.us_symbol}: ${f.reason}`).join("\n"),
      });
    } else {
      toast.success(`${added} positions added to your portfolio`);
    }
    onHoldingsChanged?.();
    if (added > 0) onClose?.();
  };

  const styleKey = (investmentStyle || "balanced").toLowerCase();
  const styleMeta = STYLE_META[styleKey] || STYLE_META.balanced;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent
        className="sm:max-w-3xl max-h-[92vh] overflow-y-auto"
        data-testid="us-ai-suggestion-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-primary" />
            AI portfolio suggestion
            <Badge variant="outline" className={`ml-1 text-[10px] ${styleMeta.colour}`}>
              {styleMeta.label} style
            </Badge>
          </DialogTitle>
          <DialogDescription>
            Claude Sonnet 4.5 generates a {styleMeta.label.toLowerCase()}-tuned recommendation based
            on your current holdings, sector targets, and the live BUY/STRONG_BUY US universe.
            <span className="block mt-0.5">{styleMeta.desc}</span>
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="space-y-3" data-testid="us-ai-loading">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-48 w-full" />
          </div>
        )}

        {!loading && error && (
          <div className="rounded-md border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 mt-0.5" />
            <div>{error}</div>
          </div>
        )}

        {!loading && !error && (
          suggestion ? (
            <SuggestionView
              suggestion={suggestion}
              cooldown={cooldown}
              onRefresh={generate}
              onDismiss={dismiss}
              onAddSelected={addSelectedToPortfolio}
              refreshing={busy}
              adding={adding}
              pickState={pickState}
              onTogglePick={togglePick}
              onEditShares={editShares}
              selectedPicks={selectedPicks}
              totalCost={totalCost}
            />
          ) : (
            cooldown ? (
              <CooldownPanel
                cooldown={cooldown}
                onGenerate={generate}
                busy={busy}
              />
            ) : null
          )
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="us-ai-close-btn" disabled={adding}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default UsAiSuggestionDialog;
